import React, { useMemo, useState, useEffect, useCallback } from "react";
// ContributionDashboard import removed - not needed since Overview/Compact tabs are removed
import api from "../utils/api";
import ForecastingChart from "../components/ForecastingChart";
import HierarchyTreeView from "../components/HierarchyTreeView";
import { classifyError, shouldShowError, isFatalError } from "../utils/errorClassifier";
import { ErrorBanner, FullErrorState } from "../components/ErrorComponents";

const REFRESH_PRESETS = [
  { label: "5s", value: 5000 },
  { label: "10s", value: 10000 },
  { label: "15s", value: 15000 },
  { label: "30s", value: 30000 },
];

// ========== TREE HELPERS ==========
const normalizeTree = (node) => {
  if (!node) return null;
  let childrenArray = [];
  if (Array.isArray(node.children)) {
    childrenArray = node.children.map(normalizeTree);
  } else if (typeof node.children === "object" && node.children !== null) {
    childrenArray = Object.values(node.children).map(normalizeTree);
  }
  return { ...node, children: childrenArray };
};

const isSourceCategory = (cat) =>
  ["GRID", "SOLAR", "GENERATOR"].includes((cat || "").toUpperCase());

const collectAllMachines = (node) => {
  const machines = [];
  const walk = (n) => {
    if (n.machine_name) {
      machines.push({
        name: n.machine_name,
        meter_id: n.meter_id || null,
        category: (n.category || "MACHINE").toUpperCase(),
        is_virtual: !!n.is_virtual,
      });
    }
    (n.children || []).forEach(walk);
  };
  if (node) walk(node);
  return machines;
};

// Only Channel Checker view is needed - Overview and Compact tabs removed

const statusAccent = {
  success: "#22c55e",
  error: "#ef4444",
  pending: "#f97316",
};

// ========== Channel Checker Helper Functions ==========
const getStatus = (powerKw, _isStale, category) => {
  if (powerKw === null || powerKw === undefined) return "OFF";
  const threshold = category === "GENERATOR" ? 0.5 : 0.05;
  if (powerKw <= threshold) return "OFF";
  return "ON";
};

const getMeterStatus = (isStale, isVirtual) => {
  if (isVirtual) return "-";
  return isStale ? "offline" : "online";
};

// Health status tooltips
const healthStatusTooltips = {
  Normal: "No issues detected!",
  Warning: "Machine energy consumption is increasing over time",
  Critical:
    "Machine load has exceeded its rated power. Needs urgent maintenance",
};

// Badge component for Channel Checker
const StatusBadge = ({ value, type }) => {
  const getColor = () => {
    if (type === "status") {
      return value === "ON" ? "#90EE90" : "#FA8072";
    }
    if (type === "meterStatus") {
      if (value === "online") return "#90EE90";
      if (value === "idle") return "#FFD700";
      if (value === "offline") return "#FA8072";
      return "transparent";
    }
    if (type === "healthStatus") {
      if (value === "Normal") return "#90EE90";
      if (value === "Warning") return "#FFD700";
      if (value === "Critical") return "#FA8072";
      return "transparent";
    }
    return "transparent";
  };

  const getTooltip = () => {
    if (type === "healthStatus" && healthStatusTooltips[value]) {
      return healthStatusTooltips[value];
    }
    return null;
  };

  if (value === "-") {
    return <span>-</span>;
  }

  return (
    <span
      style={{
        backgroundColor: getColor(),
        borderRadius: "4px",
        padding: "2px 12px",
        display: "inline-block",
        fontWeight: 500,
        cursor: "default",
      }}
      title={getTooltip()}
    >
      {value}
    </span>
  );
};

// Display value for table cells; virtual grouping rows omit placeholder dashes
const formatTableCell = (value, isVirtual) => {
  if (value === "-" || value == null || value === "") {
    return isVirtual ? "" : "-";
  }
  return value;
};

const getVirtualCategoryLabel = (category) => {
  const cat = (category || "").trim().toUpperCase();
  return cat || "VIRTUAL";
};

const VirtualCategoryLabel = ({ category }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      color: "#0f766e",
      backgroundColor: "#d1fae5",
      border: "1px solid #99f6e4",
      borderRadius: 4,
      padding: "2px 6px",
      flexShrink: 0,
    }}
    title="Node category"
  >
    {getVirtualCategoryLabel(category)}
  </span>
);

// ========== Channel Checker Component with Hierarchy ==========
const ChannelCheckerContent = ({
  hierarchyTree,
  latestData,
  healthStatusData,
  loading,
  error,
  username,
  unitPrice,
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedLoad, setSelectedLoad] = useState("all");
  const [viewMode, setViewMode] = useState("hierarchy"); // "hierarchy" or "flat"
  const [sortColumn, setSortColumn] = useState("power");
  const [sortDirection, setSortDirection] = useState("desc");

  // Build meter_index for hierarchy tree nodes that share a meter_id.
  // Walks the tree in order (matching DB insertion order) and assigns
  // index 1, 2, 3... to nodes sharing the same meter_id.
  const meterIndexMap = useMemo(() => {
    if (!hierarchyTree) return new Map();
    const indexMap = new Map(); // machine_name → index (1-based)
    const meterCounts = {}; // meter_id → count of nodes seen so far
    const walk = (node) => {
      if (node.meter_id) {
        meterCounts[node.meter_id] = (meterCounts[node.meter_id] || 0) + 1;
      }
      (node.children || []).forEach(walk);
    };
    // First pass: count how many nodes share each meter_id
    const totals = {};
    const countAll = (node) => {
      if (node.meter_id)
        totals[node.meter_id] = (totals[node.meter_id] || 0) + 1;
      (node.children || []).forEach(countAll);
    };
    countAll(hierarchyTree);
    // Second pass: assign index only to shared meter_id nodes
    const seen = {};
    const assign = (node) => {
      const mid = node.meter_id;
      if (mid && totals[mid] > 1) {
        seen[mid] = (seen[mid] || 0) + 1;
        indexMap.set(node.machine_name, seen[mid]);
      }
      (node.children || []).forEach(assign);
    };
    assign(hierarchyTree);
    return indexMap;
  }, [hierarchyTree]);

  // Create readings map keyed by meter_id (or meter_id_index for shared meters)
  // API returns meter_index for shared meters (1, 2, 3 based on DB order)
  const latestReadingsMap = useMemo(() => {
    if (!latestData?.readings) return new Map();
    const map = new Map();
    latestData.readings.forEach((r) => {
      const mid = r.meter_id;
      if (!mid) return;
      const key = r.meter_index != null ? `${mid}_${r.meter_index}` : mid;
      map.set(key, r);
    });
    return map;
  }, [latestData]);

  // Get list of all available loads/machines for dropdown
  const availableLoads = useMemo(() => {
    if (hierarchyTree) {
      return collectAllMachines(hierarchyTree);
    }
    if (latestData?.readings) {
      return latestData.readings.map((r) => ({
        name: r.machine_name || r.appliance_name,
        meter_id: r.meter_id || null,
        category: "MACHINE",
        is_virtual: false,
      }));
    }
    return [];
  }, [hierarchyTree, latestData]);

  // Helper to get reading data for a machine
  // Shared meter → lookup by meter_id_index, non-shared → lookup by meter_id
  const getReadingData = useCallback(
    (machineName, category, meterId) => {
      const idx = meterId ? meterIndexMap.get(machineName) : undefined;
      const key = meterId
        ? idx != null
          ? `${meterId}_${idx}`
          : meterId
        : null;
      const reading = key ? latestReadingsMap.get(key) : null;
      if (!reading) {
        return {
          timestamp: "-",
          power: "-",
          current: "-",
          voltage: "-",
          powerFactor: "-",
          frequency: "-",
          thdVoltage: "-",
          thdCurrent: "-",
          tap: "-",
          status: "-",
          meterStatus: "-",
          healthStatus: "-",
        };
      }
      // Virtual nodes only have calculated power — no physical meter readings
      if (reading.is_virtual) {
        return {
          timestamp: "-",
          power: reading.power_kw?.toFixed(2) ?? "-",
          current: "-",
          voltage: "-",
          powerFactor: "-",
          frequency: "-",
          thdVoltage: "-",
          thdCurrent: "-",
          tap: "-",
          status: "-",
          meterStatus: "-",
          healthStatus: "-",
          phase_type: null,
          phases: null,
        };
      }
      return {
        timestamp: reading.timestamp,
        power: reading.power_kw?.toFixed(2) ?? "-",
        current: reading.current?.toFixed(1) ?? "-",
        voltage: reading.voltage?.toFixed(2) ?? "-",
        powerFactor: reading.power_factor?.toFixed(2) ?? "-",
        frequency: reading.frequency?.toFixed(2) ?? "-",
        thdVoltage: reading.thd_voltage?.toFixed(2) ?? "-",
        thdCurrent: reading.thd_current?.toFixed(2) ?? "-",
        tap: reading.tap?.toFixed(2) ?? "-",
        status: getStatus(reading.power_kw, reading.is_stale, category),
        meterStatus: getMeterStatus(reading.is_stale, reading.is_virtual),
        healthStatus: healthStatusData?.[key] || "-",
        phase_type: reading.phase_type || null,
        phases: reading.phases || null,
      };
    },
    [latestReadingsMap, healthStatusData, meterIndexMap],
  );

  // Build hierarchical rows by walking the tree recursively
  const hierarchicalData = useMemo(() => {
    if (!hierarchyTree) {
      if (!latestData?.readings) return [];
      return latestData.readings.map((reading) => {
        const machineName =
          reading.machine_name || reading.appliance_name || "Unknown";
        return {
          id: `node-${machineName}`,
          name: machineName,
          level: 0,
          hasChildren: false,
          isVirtual: !!reading.is_virtual,
          ...getReadingData(machineName, undefined, reading.meter_id),
        };
      });
    }

    const rows = [];
    const walk = (node, depth) => {
      const name = node.machine_name || "Unknown";
      const id = `node-${name}`;
      const children = node.children || [];
      const hasChildren = children.length > 0;
      const cat = (node.category || "").toUpperCase();
      let reading = getReadingData(name, cat, node.meter_id);

      // Virtual node with no API reading — calculate power from children
      if (node.is_virtual && reading.power === "-" && hasChildren) {
        let calcPower = 0;
        const sumChildren = (n) => {
          const nIdx = n.meter_id
            ? meterIndexMap.get(n.machine_name)
            : undefined;
          const rKey = n.meter_id
            ? nIdx != null
              ? `${n.meter_id}_${nIdx}`
              : n.meter_id
            : null;
          const r = rKey ? latestReadingsMap.get(rKey) : null;
          if (r) {
            const pw = parseFloat(r.power_kw) || 0;
            const c = (n.category || "").toUpperCase();
            calcPower += c === "SOLAR" || c === "GENERATOR" ? Math.abs(pw) : pw;
          } else {
            (n.children || []).forEach(sumChildren);
          }
        };
        children.forEach(sumChildren);
        reading = { ...reading, power: calcPower.toFixed(2) };
      }

      rows.push({
        id,
        name,
        level: depth,
        hasChildren,
        ...reading,
        isSource: isSourceCategory(cat),
        isVirtual: !!node.is_virtual,
        category: cat,
      });

      if (hasChildren && expandedNodes.has(id)) {
        children.forEach((child) => walk(child, depth + 1));
      }
    };

    walk(hierarchyTree, 0);
    return rows;
  }, [hierarchyTree, latestData, expandedNodes, getReadingData]);

  // Flat data view - all machines without hierarchy
  const flatData = useMemo(() => {
    return availableLoads.map((load) => {
      const readingData = getReadingData(load.name, undefined, load.meter_id);
      return {
        id: `flat-${load.name}`,
        name: load.name,
        level: 0,
        hasChildren: false,
        isVirtual: !!load.is_virtual,
        category: load.category,
        ...readingData,
      };
    });
  }, [availableLoads, getReadingData]);

  // Filter data based on selected load
  const filteredData = useMemo(() => {
    if (selectedLoad === "all") {
      return viewMode === "hierarchy" ? hierarchicalData : flatData;
    }
    const selectedLoadInfo = availableLoads.find(
      (l) => l.name === selectedLoad,
    );
    if (selectedLoadInfo) {
      const readingData = getReadingData(
        selectedLoad,
        undefined,
        selectedLoadInfo.meter_id,
      );
      const rows = [
        {
          id: `selected-${selectedLoad}`,
          name: selectedLoad,
          level: 0,
          hasChildren: false,
          isVirtual: !!selectedLoadInfo.is_virtual,
          category: selectedLoadInfo.category,
          ...readingData,
        },
      ];

      // Add phase rows if 3-phase meter (like the old code did)
      if (readingData.phases && readingData.phase_type === 3) {
        ["phase1", "phase2", "phase3"].forEach((phaseKey, idx) => {
          const phase = readingData.phases[phaseKey];
          if (phase) {
            rows.push({
              id: `phase-${selectedLoad}-${idx}`,
              name: `${selectedLoad} phase ${idx + 1}`,
              level: 0,
              hasChildren: false,
              isVirtual: false,
              timestamp: readingData.timestamp,
              power: phase.power?.toFixed(2) ?? "-",
              current: phase.current?.toFixed(1) ?? "-",
              voltage: phase.voltage?.toFixed(2) ?? "-",
              powerFactor: phase.power_factor?.toFixed(2) ?? "-",
              frequency: "-",
              thdVoltage: phase.thd_voltage?.toFixed(2) ?? "-",
              thdCurrent: phase.thd_current?.toFixed(2) ?? "-",
              tap: "-",
              status: readingData.status,
              meterStatus: readingData.meterStatus,
              healthStatus: readingData.healthStatus,
            });
          }
        });
      }
      return rows;
    }
    return [];
  }, [
    hierarchicalData,
    flatData,
    selectedLoad,
    availableLoads,
    getReadingData,
    viewMode,
  ]);

  // Sort the filtered data
  const sortedData = useMemo(() => {
    if (!filteredData.length) return [];
    if (viewMode === "hierarchy" && selectedLoad === "all") return filteredData;

    return [...filteredData].sort((a, b) => {
      if (sortColumn === "name") {
        const aVal = (a.name || "").toLowerCase();
        const bVal = (b.name || "").toLowerCase();
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      const numericColumns = [
        "power",
        "current",
        "voltage",
        "powerFactor",
        "frequency",
        "thdVoltage",
        "thdCurrent",
        "tap",
      ];
      if (numericColumns.includes(sortColumn)) {
        const aVal = parseFloat(a[sortColumn]) || 0;
        const bVal = parseFloat(b[sortColumn]) || 0;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aVal = (a[sortColumn] || "").toString().toLowerCase();
      const bVal = (b[sortColumn] || "").toString().toLowerCase();
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredData, sortColumn, sortDirection, viewMode, selectedLoad]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const isSortingEnabled = viewMode === "flat" || selectedLoad !== "all";

  const SortHeader = ({ column, children, align }) => (
    <th
      style={{
        ...channelStyles.th,
        cursor: isSortingEnabled ? "pointer" : "default",
        userSelect: "none",
        textAlign: align || "center",
      }}
      onClick={() => isSortingEnabled && handleSort(column)}
    >
      {children}
      {isSortingEnabled && (
        <span
          style={{ marginLeft: 4, opacity: sortColumn === column ? 1 : 0.3 }}
        >
          {sortColumn === column ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
        </span>
      )}
    </th>
  );

  // Expand all nodes by default on first load
  useEffect(() => {
    if (hierarchyTree && expandedNodes.size === 0) {
      const allNodeIds = new Set();
      const walk = (node) => {
        const children = node.children || [];
        if (children.length > 0) {
          allNodeIds.add(`node-${node.machine_name}`);
          children.forEach(walk);
        }
      };
      walk(hierarchyTree);
      setExpandedNodes(allNodeIds);
    }
  }, [hierarchyTree, expandedNodes.size]);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const renderHierarchicalTable = () => {
    if (loading) {
      return <div style={channelStyles.loading}>Loading...</div>;
    }

    if (error && sortedData.length === 0) {
      return <div style={channelStyles.error}>{error}</div>;
    }

    if (sortedData.length === 0) {
      return <div style={channelStyles.noData}>No data available</div>;
    }

    return (
      <div style={channelStyles.tableContainer}>
        {/* Controls Row: View Toggle + Filter Dropdown */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          {/* View Mode Toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
              View:
            </label>
            <div
              style={{
                display: "flex",
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid #d1d5db",
              }}
            >
              <button
                type="button"
                onClick={() => setViewMode("hierarchy")}
                style={{
                  padding: "6px 14px",
                  border: "none",
                  backgroundColor:
                    viewMode === "hierarchy" ? "#1BA08E" : "#fff",
                  color: viewMode === "hierarchy" ? "#fff" : "#374151",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Hierarchy
              </button>
              <button
                type="button"
                onClick={() => setViewMode("flat")}
                style={{
                  padding: "6px 14px",
                  border: "none",
                  borderLeft: "1px solid #d1d5db",
                  backgroundColor: viewMode === "flat" ? "#1BA08E" : "#fff",
                  color: viewMode === "flat" ? "#fff" : "#374151",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Flat (Sortable)
              </button>
            </div>
          </div>

          {/* Filter Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
              Filter:
            </label>
            <select
              value={selectedLoad}
              onChange={(e) => setSelectedLoad(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                fontSize: 14,
                minWidth: 180,
                cursor: "pointer",
                backgroundColor: "#fff",
              }}
            >
              <option value="all">All Equipment</option>
              {availableLoads.map((load) => (
                <option key={load.name} value={load.name}>
                  {load.name}
                </option>
              ))}
            </select>
            {selectedLoad !== "all" && (
              <button
                type="button"
                onClick={() => setSelectedLoad("all")}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f3f4f6",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div style={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto", overflowX: "auto" }}>
          <table style={channelStyles.table}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <tr>
              <SortHeader column="name" align="left">
                Name
              </SortHeader>
              <th style={channelStyles.th}>Time</th>
              <SortHeader column="power">Power (kW)</SortHeader>
              <SortHeader column="current">Current (A)</SortHeader>
              <SortHeader column="voltage">Voltage (V)</SortHeader>
              <SortHeader column="powerFactor">Power Factor</SortHeader>
              <SortHeader column="frequency">Frequency (Hz)</SortHeader>
              <SortHeader column="thdVoltage">THD V (%)</SortHeader>
              <SortHeader column="thdCurrent">THD I (%)</SortHeader>
              <SortHeader column="tap">TAP (kW)</SortHeader>
              <th style={channelStyles.th}>Status</th>
              <th style={channelStyles.th}>Meter Status</th>
              <th style={channelStyles.th}>Health Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row) => {
              const indentPadding = 12 + row.level * 24;
              const isExpanded = expandedNodes.has(row.id);
              const isVirtual = !!row.isVirtual;
              const levelColors = [
                "#f0f9ff",
                "#fefce8",
                "#f0fdf4",
                "#fff",
                "#faf5ff",
              ];
              const rowBackground = levelColors[row.level] || "#fff";

              const rowHoverHandlers = {
                onMouseEnter: (e) => {
                  e.currentTarget.style.filter = "brightness(0.97)";
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.filter = "none";
                },
              };

              const expandControl = row.hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleNode(row.id);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    fontSize: 12,
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 4,
                    flexShrink: 0,
                  }}
                >
                  {isExpanded ? "▼" : "▶"}
                </button>
              ) : (
                <span style={{ width: 20, display: "inline-block", flexShrink: 0 }} />
              );

              if (isVirtual) {
                const powerText = formatTableCell(row.power, true);
                const virtualGapCell = {
                  ...channelStyles.td,
                  ...channelStyles.tdVirtualGap,
                };
                return (
                  <tr
                    key={row.id}
                    style={{ ...channelStyles.tr, ...channelStyles.trVirtual }}
                    {...rowHoverHandlers}
                  >
                    <td
                      style={{
                        ...channelStyles.td,
                        ...channelStyles.tdVirtualName,
                        paddingLeft: indentPadding,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {expandControl}
                        <span>{row.name}</span>
                        <VirtualCategoryLabel category={row.category} />
                      </div>
                    </td>
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={{ ...channelStyles.td, fontWeight: 600 }}>
                      {powerText || null}
                    </td>
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                    <td style={virtualGapCell} aria-hidden="true" />
                  </tr>
                );
              }

              const timeDisplay =
                !row.timestamp || row.timestamp === "-"
                  ? "More than 3 hours"
                  : row.timestamp;

              return (
                <tr
                  key={row.id}
                  style={{
                    ...channelStyles.tr,
                    backgroundColor: rowBackground,
                  }}
                  {...rowHoverHandlers}
                >
                  <td
                    style={{
                      ...channelStyles.td,
                      textAlign: "left",
                      paddingLeft: indentPadding,
                      fontWeight: row.level < 3 ? 600 : 400,
                      verticalAlign: "middle",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      {expandControl}
                      {row.name}
                    </div>
                  </td>
                  <td style={channelStyles.td}>{timeDisplay}</td>
                  <td style={{ ...channelStyles.td, fontWeight: 600 }}>
                    {row.power || "-"}
                  </td>
                  <td style={channelStyles.td}>{row.current || "-"}</td>
                  <td style={channelStyles.td}>{row.voltage || "-"}</td>
                  <td style={channelStyles.td}>
                    {(() => {
                      const pfNum = parseFloat(row.powerFactor);
                      if (!Number.isFinite(pfNum)) return row.powerFactor || "-";
                      let color = "#16a34a"; // green: > 0.8
                      if (pfNum < 0.7) color = "#dc2626"; // red
                      else if (pfNum <= 0.8) color = "#f59e0b"; // orange
                      return (
                        <span style={{ color, fontWeight: 700 }}>
                          {row.powerFactor}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={channelStyles.td}>{row.frequency || "-"}</td>
                  <td style={channelStyles.td}>{row.thdVoltage || "-"}</td>
                  <td style={channelStyles.td}>{row.thdCurrent || "-"}</td>
                  <td style={channelStyles.td}>{row.tap || "-"}</td>
                  <td style={channelStyles.td}>
                    <StatusBadge value={row.status || "-"} type="status" />
                  </td>
                  <td style={channelStyles.td}>
                    <StatusBadge
                      value={row.meterStatus || "-"}
                      type="meterStatus"
                    />
                  </td>
                  <td style={channelStyles.td}>
                    <StatusBadge
                      value={row.healthStatus || "-"}
                      type="healthStatus"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Use API's totalUsage (null if any meter is offline > 3 hours)
  const totalUsage = latestData?.totalUsage;
  // Count stale from individual readings (source of truth) — API summary can lag
  const staleCount = (latestData?.readings || []).filter(
    (r) => r.is_stale && !r.is_virtual,
  ).length;
  const isUsageComplete = staleCount === 0;
  const usageTimestamp = latestData?.usageTimestamp || null;

  const costPerHour =
    totalUsage != null ? (totalUsage * unitPrice).toFixed(0) : "--";

  const formattedTime = usageTimestamp ? usageTimestamp : "-";

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 20,
          marginBottom: 23,
          maxWidth: 700,
          margin: "0 auto 23px auto",
        }}
      >
        <div
          style={{
            flex: 1,
            background: "linear-gradient(135deg, #F5A623 0%, #E09100 100%)",
            borderRadius: 12,
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            boxShadow: "0 4px 15px rgba(245, 166, 35, 0.3)",
            cursor: "default",
          }}
          title={
            "This is the most recent power\nreported by your smart meter.\nThe exact time is shown below."
          }
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
          </div>
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.5px",
              }}
            >
              {totalUsage != null ? `${totalUsage.toFixed(2)} kW` : "-- kW"}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.9)",
                marginTop: 2,
              }}
            >
              {`as of ${formattedTime}`}
              {!isUsageComplete && (
                <span style={{ marginLeft: 6, fontSize: 12, fontWeight: 600 }}>
                  | {staleCount} meters offline
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            background: "linear-gradient(135deg, #0288D1 0%, #0277BD 100%)",
            borderRadius: 12,
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            boxShadow: "0 4px 15px rgba(2, 136, 209, 0.3)",
            cursor: "default",
          }}
          title={
            "This is the cost that will be added\nto your monthly bill if you continue\nto use at the same rate for one hour."
          }
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              <path d="M12 8v8"></path>
              <path d="M8 12h8"></path>
            </svg>
          </div>
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#fff",
                letterSpacing: "-0.5px",
              }}
            >
              Rs. {costPerHour}
            </div>
            <div
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.9)",
                marginTop: 2,
              }}
            >
              per Hour
            </div>
          </div>
        </div>
      </div>

      <div style={channelStyles.content}>{renderHierarchicalTable()}</div>
    </div>
  );
};

const channelStyles = {
  content: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "20px",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  th: {
    padding: "12px 8px",
    textAlign: "center",
    borderBottom: "2px solid #dee2e6",
    backgroundColor: "#f8f9fa",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  tr: {
    transition: "background-color 0.2s",
  },
  td: {
    padding: "10px 8px",
    textAlign: "center",
    borderBottom: "1px solid #dee2e6",
  },
  trVirtual: {
    backgroundColor: "#f4f7f6",
  },
  tdVirtualName: {
    textAlign: "left",
    verticalAlign: "middle",
    padding: "11px 8px",
    fontWeight: 700,
    fontSize: 14,
    color: "#134e4a",
    borderBottom: "1px solid #dfe6e4",
    boxShadow: "inset 3px 0 0 #1BA08E",
  },
  tdVirtualGap: {
    padding: "11px 4px",
    borderBottom: "1px solid #dfe6e4",
    backgroundColor: "#f4f7f6",
  },
  machineCell: {
    padding: "10px 8px",
    textAlign: "right",
    borderBottom: "1px solid #dee2e6",
    color: "#6c757d",
    fontWeight: "bold",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
  error: {
    textAlign: "center",
    padding: "40px",
    color: "#ef4444",
  },
  noData: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
};

// ========== Main LiveTab Component ==========
const LiveTab = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [manualRefreshToken, setManualRefreshToken] = useState(0);
  const [lastUpdateMeta, setLastUpdateMeta] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const pending = sessionStorage.getItem("enviz-pending-tab");
    if (pending) {
      sessionStorage.removeItem("enviz-pending-tab");
      return pending;
    }
    return "diagram";
  }); // "table", "diagram", or "forecast"
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Shared data — fetched once, used by both Table and Diagram views
  const [sharedHierarchyTree, setSharedHierarchyTree] = useState(null);
  const [sharedLatestData, setSharedLatestData] = useState(null);
  const [sharedHealthStatus, setSharedHealthStatus] = useState(null);
  const [sharedLoading, setSharedLoading] = useState(true);
  
  // Per-endpoint error tracking
  const [hierarchyError, setHierarchyError] = useState(null);
  const [latestError, setLatestError] = useState(null);
  const [healthError, setHealthError] = useState(null);

  const unitPrice = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const price = parseFloat(user?.unit_price);
      return Number.isFinite(price) ? price : 0;
    } catch {
      return 0;
    }
  }, []);

  const username = localStorage.getItem("username");

  const fetchSharedData = useCallback(async () => {
    if (!username) {
      const noUserErr = {
        status: 'auth',
        message: 'No username found in session.',
        retryable: false,
        endpoint: 'session',
        severity: 'error'
      };
      setHierarchyError(noUserErr);
      setLatestError(noUserErr);
      setHealthError(noUserErr);
      setSharedLoading(false);
      setLastUpdateMeta({ status: "error", timestamp: Date.now() });
      return;
    }

    setLastUpdateMeta({ status: "pending", timestamp: Date.now() });

    // Use Promise.allSettled to fetch all endpoints independently
    const results = await Promise.allSettled([
      api.get(`/hierarchy-tree`, { params: { username } }),
      api.get(`/latest/${username}`),
      api.get(`/status/${username}`),
    ]);

    let hasAllFailed = false;

    // ===== Process hierarchy-tree result =====
    if (results[0].status === 'fulfilled') {
      const rawTree = results[0].value.data?.data || results[0].value.data || null;
      if (rawTree) {
        setSharedHierarchyTree((prev) => normalizeTree(rawTree) || prev);
        setHierarchyError(null);
      }
    } else {
      const err = classifyError(results[0].reason, 'hierarchy-tree');
      setHierarchyError(err);
      console.warn("Hierarchy tree fetch failed:", err);
    }

    // ===== Process latest result =====
    if (results[1].status === 'fulfilled') {
      setSharedLatestData((prev) => results[1].value.data || prev);
      setLatestError(null);
    } else {
      const err = classifyError(results[1].reason, 'latest');
      setLatestError(err);
      console.warn("Latest data fetch failed:", err);
    }

    // ===== Process health status result =====
    if (results[2].status === 'fulfilled') {
      const healthData = results[2].value.data || null;
      // Empty {} is valid for health status (means no health data)
      setSharedHealthStatus((prev) => healthData !== null ? healthData : prev);
      setHealthError(null);
    } else {
      const err = classifyError(results[2].reason, 'status');
      // Silent errors (severity='silent') should not set error state
      if (err.severity !== 'silent') {
        setHealthError(err);
      } else {
        setHealthError(null);
      }
      console.warn("Health status fetch failed:", err);
    }

    // Determine overall status
    const hasFatalError = 
      (hierarchyError && isFatalError(hierarchyError)) ||
      (latestError && isFatalError(latestError));

    if (hasFatalError) {
      setLastUpdateMeta({ status: "error", timestamp: Date.now() });
    } else if (hierarchyError?.severity !== 'silent' && latestError?.severity !== 'silent') {
      // Both critical endpoints failed
      hasAllFailed = true;
      setLastUpdateMeta({ status: "error", timestamp: Date.now() });
    } else if (hierarchyError || latestError) {
      // Partial failure (one endpoint down, but data available)
      setLastUpdateMeta({ status: "partial", timestamp: Date.now() });
    } else {
      // All critical endpoints succeeded
      setLastUpdateMeta({ status: "success", timestamp: Date.now() });
    }

    setSharedLoading(false);
  }, [username, hierarchyError, latestError]);

  useEffect(() => {
    fetchSharedData();
  }, [fetchSharedData]);
  useEffect(() => {
    if (!autoRefresh || !refreshInterval) return;
    const iv = setInterval(fetchSharedData, refreshInterval);
    return () => clearInterval(iv);
  }, [autoRefresh, refreshInterval, fetchSharedData]);
  useEffect(() => {
    if (manualRefreshToken > 0) fetchSharedData();
  }, [manualRefreshToken, fetchSharedData]);

  const statusText = useMemo(() => {
    if (!lastUpdateMeta) return "Awaiting first sync";
    if (lastUpdateMeta.status === "success") {
      return `Live`;
    }
    if (lastUpdateMeta.status === "error") {
      return "Sync failed — tap refresh";
    }
    return "Refreshing...";
  }, [lastUpdateMeta]);

  const statusColor = useMemo(() => {
    if (!lastUpdateMeta) return statusAccent.pending;
    return statusAccent[lastUpdateMeta.status] || statusAccent.pending;
  }, [lastUpdateMeta]);

  const handleManualRefresh = () => {
    setLastUpdateMeta({ status: "pending", timestamp: Date.now() });
    setManualRefreshToken((token) => token + 1);
  };

  // Listen for refresh event from Header
  useEffect(() => {
    const onHeaderRefresh = () => handleManualRefresh();
    window.addEventListener("enviz-refresh", onHeaderRefresh);
    return () => window.removeEventListener("enviz-refresh", onHeaderRefresh);
  }, []);

  // Listen for tab change event from Header dropdown
  useEffect(() => {
    const onTabChange = (e) => setActiveTab(e.detail);
    window.addEventListener("enviz-tab-change", onTabChange);
    return () => window.removeEventListener("enviz-tab-change", onTabChange);
  }, []);

  // Determine if both critical endpoints failed
  const isCriticalFailure = 
    latestError && 
    latestError.severity !== 'silent' &&
    hierarchyError &&
    hierarchyError.severity !== 'silent';

  // Handle 401 redirect
  useEffect(() => {
    if (latestError?.status === 401 || hierarchyError?.status === 401) {
      console.warn("Authentication error detected, redirecting to login");
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
  }, [latestError, hierarchyError]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 10 }}>
      
      {/* Show full error state if both critical endpoints fail */}
      {isCriticalFailure && (
        <FullErrorState
          error={latestError}
          onRetry={handleManualRefresh}
          loading={sharedLoading}
        />
      )}

      {/* If critical endpoints have data, show with optional warning banners */}
      {!isCriticalFailure && (
        <>
          {/* Warning banner for partial failures */}
          {hierarchyError && shouldShowError(hierarchyError) && (
            <ErrorBanner
              error={hierarchyError}
              onRetry={handleManualRefresh}
              onDismiss={() => setHierarchyError(null)}
            />
          )}
          {latestError && shouldShowError(latestError) && (
            <ErrorBanner
              error={latestError}
              onRetry={handleManualRefresh}
              onDismiss={() => setLatestError(null)}
            />
          )}

          {/* Main content views */}
          {activeTab === "table" ? (
            <ChannelCheckerContent
              hierarchyTree={sharedHierarchyTree}
              latestData={sharedLatestData}
              healthStatusData={sharedHealthStatus}
              loading={sharedLoading}
              error={null}
              username={username}
              unitPrice={unitPrice}
              healthError={healthError}
            />
          ) : activeTab === "diagram" ? (
            <HierarchyTreeView
              hierarchyTree={sharedHierarchyTree}
              latestData={sharedLatestData}
              healthStatusData={sharedHealthStatus}
              loading={sharedLoading}
              error={null}
              username={username}
              unitPrice={unitPrice}
              healthError={healthError}
            />
          ) : activeTab === "forecast" ? (
            <ForecastingChart
              username={username}
              availableMachines={sharedHierarchyTree ? collectAllMachines(sharedHierarchyTree) : []}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default LiveTab;
